import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import usersService from "../../services/usersService";
import rolesService from "../../services/rolesService";
import { useAccessChecker } from "../../utils/Functions/Authorization";

import {
  Plus,
  MagnifyingGlass,
  FunnelSimple,
  ArrowsClockwise,
  DotsThreeVertical,
  PencilSimple,
  Trash,
  X,
  ArrowRight,
  WarningCircle,
  Spinner,
  EnvelopeSimple,
  Shield,
  Users as UsersIcon,
  User as UserIcon,
  CheckCircle,
  XCircle,
} from "phosphor-react";

import { DeleteModal, SearchableSelect } from "../../components";
import UserFormModal from "./UserFormModal";
import AssignmentModal from "./AssignmentModal";

/* -------------------- utils -------------------- */
function cx() {
  return Array.from(arguments).filter(Boolean).join(" ");
}

/* -------------------- motion -------------------- */
const fadeUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.22 } },
};

const dropdownAnim = {
  initial: { opacity: 0, scale: 0.98, y: 4 },
  animate: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.12 } },
  exit: { opacity: 0, scale: 0.98, y: 4, transition: { duration: 0.1 } },
};

const panelAnim = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto", transition: { duration: 0.16 } },
  exit: { opacity: 0, height: 0, transition: { duration: 0.14 } },
};

export default function UsersRedesign() {
  const { t } = useTranslation();
  const { checkAccess } = useAccessChecker();

  // Data
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ OLD LOGIC (do not change)
  const [filters, setFilters] = useState({ status: "", role: "", searchValue: "" });
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Modals
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [userFormOpen, setUserFormOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignManager, setAssignManager] = useState(null);

  // ✅ Dropdown Portal
  const [dropdownOpen, setDropdownOpen] = useState(null); // userId
  const [dropdownUser, setDropdownUser] = useState(null); // keep user object (fix assign button)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [anchorRect, setAnchorRect] = useState(null);

  const menuRef = useRef(null);
  const btnRefs = useRef(new Map()); // userId -> button

  const setBtnRef = useCallback((userId) => {
    return (node) => {
      if (!node) return btnRefs.current.delete(userId);
      btnRefs.current.set(userId, node);
    };
  }, []);

  /* -------------------- permissions (OLD LOGIC) -------------------- */
  const canCreate = useMemo(() => checkAccess("users", "create"), [checkAccess]);
  const canEdit = useMemo(() => checkAccess("users", "update"), [checkAccess]);
  const canDelete = useMemo(() => checkAccess("users", "delete"), [checkAccess]);
  const showActions = canEdit || canDelete; // ✅ no assignments permission

  /* -------------------- options (keep backend values) -------------------- */
  const statusOptions = useMemo(
    () => [
      { id: 1, name: t("All Statuses"), value: "" },
      { id: 2, name: t("Active"), value: "active" },
      { id: 3, name: t("Inactive"), value: "inactive" }, // ✅ keep exact
      { id: 4, name: t("Suspended"), value: "suspended" },
    ],
    [t]
  );

  /* -------------------- derived -------------------- */
  const filtersActive = useMemo(() => {
    return (
      String(filters.searchValue || "").trim() !== "" ||
      String(filters.status || "") !== "" ||
      String(filters.role || "") !== ""
    );
  }, [filters.searchValue, filters.status, filters.role]);

  const selectedRoleLabel = useMemo(() => {
    if (!filters.role) return "";
    return roles.find((r) => r.value === filters.role)?.name || filters.role;
  }, [filters.role, roles]);

  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => String(u?.status || "").toLowerCase() === "active").length;
    const suspended = users.filter((u) => String(u?.status || "").toLowerCase() !== "active").length;
    return { total, active, suspended };
  }, [users]);

  /* -------------------- fetch (OLD LOGIC) -------------------- */
  const fetchRoles = useCallback(async () => {
    try {
      const response = await rolesService.search({ withClient: false });
      if (response?.data?.statusCode === 200) {
        const formatted = (response.data.data || []).map((r) => ({
          id: r._id,
          name: t(r.name),
          value: r._id,
          rawName: r.name,
        }));
        setRoles(formatted);
      }
    } catch (error) {
      console.error("Role fetch failed:", error?.response?.data || error);
      toast.error(t("Failed to load roles"));
    }
  }, [t]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersService.searchUsers(filters);
      if (response?.data?.statusCode === 200) {
        setUsers(response.data.data || []);
      } else {
        setUsers([]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Failed to fetch users"));
      console.error("User fetch failed:", error?.response?.data || error);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchRoles();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------- handlers (OLD LOGIC) -------------------- */
  const handleFilterChange = useCallback((key, value) => {
    setFilters((p) => ({ ...p, [key]: value }));
  }, []);

  const applyFilters = useCallback(() => fetchUsers(), [fetchUsers]);

  const resetAndRefresh = useCallback(() => {
    setFilters({ status: "", role: "", searchValue: "" });
    setTimeout(() => fetchUsers(), 0);
  }, [fetchUsers]);

  const closeDropdown = useCallback(() => {
    setDropdownOpen(null);
    setDropdownUser(null);
  }, []);

  const openUserForm = useCallback(
    (id = null) => {
      setSelectedUserId(id);
      setUserFormOpen(true);
      closeDropdown();
    },
    [closeDropdown]
  );

  const openAssignModal = useCallback(
    (user) => {
      setAssignManager(user);
      setAssignOpen(true);
      closeDropdown();
    },
    [closeDropdown]
  );

  /* -------------------- status UI -------------------- */
  const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

  const getStatusBadge = (status) => {
    const s = normalizeStatus(status);
    if (s === "active") {
      return "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-100 dark:border-green-800/60";
    }
    if (s === "suspended") {
      return "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-500 border border-red-100 dark:border-red-800/60";
    }
    if (s === "inactive") {
      return "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-300 border border-neutral-100 dark:border-neutral-800/70";
    }
    return "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-300 border border-neutral-100 dark:border-neutral-800/70";
  };

  const getStatusDot = (status) => {
    const s = normalizeStatus(status);
    if (s === "active") return "bg-green-500";
    if (s === "suspended") return "bg-red-600";
    return "bg-yellow-500";
  };

  /* -------------------- dropdown (portal) -------------------- */
  const openDropdown = useCallback((user) => {
    const btn = btnRefs.current.get(user._id);
    if (!btn) return;

    const r = btn.getBoundingClientRect();
    setAnchorRect(r);

    const menuW = 220;
    const gap = 8;
    const pad = 8;

    let left = r.right - menuW;
    if (left < pad) left = r.left;
    left = Math.min(Math.max(left, pad), window.innerWidth - menuW - pad);

    const top = r.bottom + gap;
    setDropdownPos({ top, left });
    setDropdownOpen(user._id);
    setDropdownUser(user);
  }, []);

  const toggleDropdown = useCallback(
    (user) => {
      if (dropdownOpen === user._id) return closeDropdown();
      openDropdown(user);
    },
    [dropdownOpen, closeDropdown, openDropdown]
  );

  useLayoutEffect(() => {
    if (!dropdownOpen || !menuRef.current || !anchorRect) return;

    const menu = menuRef.current;
    const pad = 8;
    const gap = 8;
    const mh = menu.getBoundingClientRect().height;

    let top = anchorRect.bottom + gap;
    if (top + mh > window.innerHeight - pad) {
      top = Math.max(pad, anchorRect.top - mh - gap);
    }

    if (Math.abs(top - dropdownPos.top) > 1) {
      setDropdownPos((p) => ({ ...p, top }));
    }
  }, [dropdownOpen, anchorRect, dropdownPos.top]);

  useEffect(() => {
    if (!dropdownOpen) return;

    const onMouseDown = (e) => {
      const menu = menuRef.current;
      const btn = btnRefs.current.get(dropdownOpen);
      if (menu && menu.contains(e.target)) return;
      if (btn && btn.contains(e.target)) return;
      closeDropdown();
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDropdown();
    };

    const onAnyScroll = () => closeDropdown();

    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onAnyScroll, true);
    window.addEventListener("resize", onAnyScroll);

    return () => {
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onAnyScroll, true);
      window.removeEventListener("resize", onAnyScroll);
    };
  }, [dropdownOpen, closeDropdown]);

  const dropdownNode =
    dropdownOpen && typeof document !== "undefined"
      ? createPortal(
        <AnimatePresence>
          <motion.div
            key="users-menu"
            ref={menuRef}
            variants={dropdownAnim}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{
              position: "fixed",
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: 220,
              zIndex: 9999,
            }}
            className="rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-900 bg-white dark:bg-neutral-900/90 py-1 backdrop-blur"
          >
            {canEdit && (
              <button
                onClick={() => openUserForm(dropdownUser?._id)}
                className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <PencilSimple size={16} className="mr-2" />
                {t("Edit")}
              </button>
            )}

            {/* ✅ OLD LOGIC: assign depends on users.update only */}
            {canEdit &&
              dropdownUser &&
              !["SuperAdmin", "Client"].includes(dropdownUser?.role?.name) && (
                <button
                  onClick={() => openAssignModal(dropdownUser)}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <UsersIcon size={16} className="mr-2" />
                  {t("Assign Clients")}
                </button>
              )}

            {canDelete && (
              <button
                onClick={() => {
                  setDeleteId(dropdownUser?._id || dropdownOpen);
                  setDeleteModal(true);
                  closeDropdown();
                }}
                className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <Trash size={16} className="mr-2" />
                {t("Delete")}
              </button>
            )}
          </motion.div>
        </AnimatePresence>,
        document.body
      )
      : null;

  /* -------------------- render -------------------- */
  return (
    <div className="lg:p-7 p-3 max-w-full mx-auto">


      {/* Top stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        <StatCard
          title={t("Total Users")}
          value={stats.total}
          icon={<UsersIcon size={18} className="text-yellow-600 dark:text-yellow-500" />}
          tone="neutral"
        />
        <StatCard
          title={t("Active Users")}
          value={stats.active}
          icon={<CheckCircle size={18} className="text-green-700 dark:text-green-500" />}
          tone="green"
        />
        <StatCard
          title={t("Inactive Users")}
          value={stats.suspended}
          icon={<XCircle size={18} className="text-red-700 dark:text-red-500" />}
          tone="red"
        />
      </div>

      {/* Search + actions bar */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        {/* Search input */}
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
          </div>

          <input
            type="text"
            placeholder={t("Search users...")}
            className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 rounded-xl placeholder-neutral-500 ring-neutral-300 dark:ring-neutral-700 bg-white dark:bg-neutral-950 text-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-1"
            value={filters.searchValue}
            onChange={(e) => handleFilterChange("searchValue", e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
          />

          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            onClick={applyFilters}
            aria-label={t("Submit search")}
          >
            <ArrowRight size={16} />
          </button>
        </div>

        {/* More filters */}
        <button
          className={cx(
            "flex items-center gap-2 px-4 h-10 border text-sm rounded-xl focus:outline-none focus:ring-1",
            showMoreFilters
              ? "border-yellow-500/50 bg-yellow-50 text-yellow-700 dark:border-yellow-600/30 dark:bg-yellow-900/20 dark:text-yellow-400"
              : "border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
          )}
          onClick={() => setShowMoreFilters((v) => !v)}
        >
          <span className="text-sm">{showMoreFilters ? t("Hide filters") : t("More filters")}</span>
          {showMoreFilters ? (
            <X size={16} className="text-current" />
          ) : (
            <FunnelSimple size={16} className="text-neutral-800 dark:text-neutral-400" />
          )}
        </button>

        {/* Reset */}
        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
          aria-label={t("Reset filters")}
        >
          <ArrowsClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">{t("Reset")}</span>
        </button>

        {/* Apply button only when filters active and panel closed */}
        {filtersActive && !showMoreFilters && (
          <button
            className="flex items-center text-xs justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-[1.02] transition"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-xs">{t("Apply Filters")}</span>
          </button>
        )}


        {canCreate && (
          <button
            className="flex items-center gap-2 px-4 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-[1.02] transition"
            onClick={() => openUserForm(null)}
          >
            <Plus size={16} />
            <span className="text-sm">{t("Create User")}</span>
          </button>
        )}
      </div>

      {/* Active Filters Chips */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {t("Active filters")}:
          </span>

          {String(filters.searchValue || "").trim() && (
            <Chip
              icon={<MagnifyingGlass size={12} />}
              label={filters.searchValue}
              onClear={() => {
                setFilters((p) => ({ ...p, searchValue: "" }));
                setTimeout(() => applyFilters(), 0);
              }}
            />
          )}

          {filters.status && (
            <Chip
              icon={<WarningCircle size={12} />}
              label={filters.status}
              onClear={() => {
                setFilters((p) => ({ ...p, status: "" }));
                setTimeout(() => applyFilters(), 0);
              }}
            />
          )}

          {filters.role && (
            <Chip
              icon={<Shield size={12} />}
              label={selectedRoleLabel}
              onClear={() => {
                setFilters((p) => ({ ...p, role: "" }));
                setTimeout(() => applyFilters(), 0);
              }}
            />
          )}

          <button
            onClick={resetAndRefresh}
            className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
          >
            <X size={12} />
            <span>{t("Clear all")}</span>
          </button>
        </div>
      )}

      {/* Expanded filters */}
      <AnimatePresence>
        {showMoreFilters && (
          <motion.div
            variants={panelAnim}
            initial="initial"
            animate="animate"
            exit="exit"
            className="mb-6"
          >
            <div className="p-6 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-[400] text-neutral-800 dark:text-white">
                  {t("More filters")}
                </h2>
                <button
                  onClick={resetAndRefresh}
                  className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1.5"
                >
                  <X size={14} />
                  <span>{t("Clear all")}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <SearchableSelect
                  options={statusOptions}
                  value={filters.status}
                  onChange={(v) => handleFilterChange("status", v)}
                  placeholder={t("Status")}
                  icon={<WarningCircle size={16} />}
                />

                <SearchableSelect
                  options={roles}
                  value={filters.role}
                  onChange={(v) => handleFilterChange("role", v)}
                  placeholder={t("Role")}
                  icon={<Shield size={16} />}
                />

                <div className="flex items-center gap-2">
                  <button
                    className="flex items-center justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-[1.02] transition"
                    onClick={applyFilters}
                  >
                    <FunnelSimple size={16} />
                    <span className="text-sm">{t("Apply Filters")}</span>
                  </button>

                  <button
                    className="flex items-center justify-center px-4 gap-2 h-10 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700"
                    onClick={resetAndRefresh}
                  >
                    <ArrowsClockwise size={16} />
                    <span className="text-sm">{t("Reset")}</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Users grid */}
      <div className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size={28} className="animate-spin text-neutral-600 dark:text-neutral-400" />
          </div>
        ) : users.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {users.map((user, i) => (
              <motion.div
                key={user._id}
                variants={fadeUp}
                initial="initial"
                animate="animate"
                transition={{ delay: i * 0.02 }}
                whileHover={{ y: 3 }}
                className="relative rounded-xl border border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-900/30 hover:shadow-md transition-all"
              >
                {/* action btn */}
                {showActions && (
                  <div className="absolute top-3 right-3 z-10">
                    <button
                      ref={setBtnRef(user._id)}
                      onClick={() => toggleDropdown(user)}
                      className="p-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-900/40 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300"
                      aria-label={t("Actions")}
                    >
                      <DotsThreeVertical size={16} />
                    </button>
                  </div>
                )}

                {/* body */}
                <div className="p-5 flex flex-col items-center">
                  <div className="relative mb-4">
                    <div className="h-16 w-16 rounded-full border-2 border-neutral-200 dark:border-neutral-900 flex items-center justify-center overflow-hidden bg-neutral-100 dark:bg-neutral-900/40">
                      {user.avatarUrl ? (
                        <img
                          src={user.avatarUrl || "/placeholder.svg"}
                          alt={user.fullName || "User"}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <UserIcon size={28} className="text-neutral-400 dark:text-neutral-500" />
                      )}
                    </div>

                    <span
                      className={cx(
                        "absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 border-white dark:border-neutral-900",
                        getStatusDot(user.status)
                      )}
                    />
                  </div>

                  <h3 className="text-md font-[500] text-neutral-900 dark:text-white mb-1 text-center">
                    {user.fullName || t("Unnamed User")}
                  </h3>

                  <div className="flex items-center text-xs text-neutral-500 dark:text-neutral-400 mb-3">
                    <EnvelopeSimple size={14} className="mr-1" />
                    <p className="truncate max-w-[170px]">{user.email || "—"}</p>
                  </div>

                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <span
                      className={cx(
                        "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-[400]",
                        getStatusBadge(user.status)
                      )}
                    >
                      <span className={cx("w-2 h-2 mr-1.5 rounded-full", getStatusDot(user.status))} />
                      {user.status || "unknown"}
                    </span>

                    <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-[400] border border-neutral-150 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/40 text-neutral-700 dark:text-neutral-300">
                      <Shield size={14} className="text-neutral-500" />
                      {user.role?.name || "User"}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
            <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
              <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
            </div>

            <h3 className="text-xl font-medium text-neutral-900 dark:text-white mb-2">
              {t("No Users Found")}
            </h3>

            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
              {filtersActive
                ? t("No users match your search criteria. Try adjusting your filters or search term.")
                : t("There are no users in the system yet.")}
            </p>

            <div className="flex gap-4">
              <button
                onClick={resetAndRefresh}
                className="px-4 py-2 border border-neutral-200 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2"
              >
                <ArrowsClockwise size={16} />
                {t("Reset Filters")}
              </button>

              {canCreate && (
                <button
                  onClick={() => openUserForm(null)}
                  className="px-4 py-2 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-[1.02] transition flex items-center gap-2"
                >
                  <Plus size={16} />
                  <span>{t("Add User")}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Portal dropdown */}
      {dropdownNode}

      {/* Modals */}
      <DeleteModal
        setDeleteId={setDeleteId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={deleteId}
        Model="users"
        setDone={() => { }}
        setLoding={setLoading}
        done={false}
      />

      <UserFormModal
        isOpen={userFormOpen}
        onClose={() => setUserFormOpen(false)}
        userId={selectedUserId}
        onSuccess={fetchUsers}
      />

      <AssignmentModal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        manager={assignManager}
        onChanged={fetchUsers}
      />
    </div>
  );
}

/* -------------------- UI pieces -------------------- */
const STAT_TONE = {
  yellow: { text: "text-neutral-600 dark:text-neutral-200", bg: "bg-yellow-100 dark:bg-yellow-900/30" },
  green: { text: "text-green-700 dark:text-green-500", bg: "bg-green-100 dark:bg-green-900/30" },
  red: { text: "text-red-700 dark:text-red-500", bg: "bg-red-100 dark:bg-red-900/30" },
};

function StatCard({ title, value, icon, tone = "yellow" }) {
  const c = STAT_TONE[tone] || STAT_TONE.yellow;
  return (
    <div className="border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-start justify-center flex-col">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">{title}</p>
          <p className={cx("text-2xl font-normal", c.text)}>
            {Number(value || 0).toLocaleString("en-US")}
          </p>
        </div>
        <div className={cx("p-2 rounded-xl", c.bg)}>{icon}</div>
      </div>
    </div>
  );
}

function Chip({ icon, label, onClear }) {
  return (
    <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
      {icon}
      <span className="max-w-[240px] truncate">{label}</span>
      <button
        type="button"
        onClick={onClear}
        className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
        aria-label="clear"
      >
        <X size={12} />
      </button>
    </div>
  );
}